import { forwardRef, useState, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { Modal, ModalMethods, toasts } from '../../../../ui-component';
import { api } from '../../../../utils/apiRequest';

type ProductDescriptionModalProps = {
  productId: number;
  description: string;
  onSaved: () => void;
};

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic'],
    [{ list: 'bullet' }, { list: 'ordered' }],
  ],
};

const QUILL_FORMATS = ['bold', 'italic', 'list', 'bullet'];

const ProductDescriptionModal = forwardRef<ModalMethods, ProductDescriptionModalProps>(
  ({ productId, description, onSaved }, ref) => {
    const [value, setValue] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
      setValue(description || '');
    }, [description]);

    const handleBack = useCallback(() => {
      if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
        ref.current.hide();
      }
    }, [ref]);

    const handleSave = useCallback(async () => {
      setIsSaving(true);

      try {
        const response = await api.putRequest(`readymade/${productId}`, {
          product_description: value,
        });

        if (response.status) {
          toasts('success', 'Product description updated successfully', 'desc-update-success');
          onSaved();

          if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.hide();
          }
        } else {
          toasts('error', response.message || 'Failed to update description', 'desc-update-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'desc-update-error');
        }
      } finally {
        setIsSaving(false);
      }
    }, [value, productId, onSaved, ref]);

    return (
      <Modal ref={ref} size="xl" showFooter={false} showCloseIcon={false}>
        <div className="flex flex-col min-h-[500px] p-4">
          <div className="flex items-center justify-between pb-4 border-b border-gray-200">
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 font-medium"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 12L6 8l4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Back
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>

          <p className="font-inter font-bold text-[15px] text-gray-900 mt-4 mb-3">
            Product Description
          </p>

          <div className="flex-1 border border-gray-200 rounded-xl overflow-hidden product-desc-editor">
            <ReactQuill
              theme="snow"
              value={value}
              onChange={setValue}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Enter product description..."
              style={{ height: 'calc(100% - 42px)' }}
            />
          </div>

          <div className="flex justify-end mt-3">
            <button
              type="button"
              className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 1.5l1.286 3.953h4.157l-3.364 2.444 1.285 3.953L8 9.406l-3.364 2.444 1.285-3.953L2.557 5.453h4.157L8 1.5z"
                  stroke="currentColor"
                  strokeWidth="1"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
              improve with ai
            </button>
          </div>
        </div>
      </Modal>
    );
  }
);

ProductDescriptionModal.displayName = 'ProductDescriptionModal';

export default ProductDescriptionModal;
